'use client';
import React, { useRef, useContext } from 'react';
import clsx from 'clsx';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  OTPInputContext,
} from '@src/v3/components/coreUI/ui/input-otp';
import { OtpInputProps } from '@src/v3/interfaces/components/otpInput';
import { OTP_VARIANTS } from '@src/v3/constants/configs';

// Custom slot component that applies focus styles based on isActive
const SlotWithFocus: React.FC<{
  index: number;
  slotClasses: string;
  error: boolean;
  dataAttributes: Record<string, number>;
}> = ({ index, slotClasses, error, dataAttributes }) => {
  const inputOTPContext = useContext(OTPInputContext);
  const { isActive } = inputOTPContext.slots[index];

  const activeClasses = clsx(
    slotClasses,
    isActive && !error && '!border-gray-1000',
    isActive && error && '!border-red-700'
  );

  return (
    <InputOTPSlot
      index={index}
      className={activeClasses}
      style={{ touchAction: 'manipulation' }}
      {...dataAttributes}
    />
  );
};

const OtpInput: React.FC<OtpInputProps> = ({
  value = '',
  onChange,
  onComplete,
  length = 6,
  error = false,
  disabled = false,
  autoFocus = false,
  dataAttribute = 'otp-index',
  className,
  inputClassName,
  onErrorClear,
  variant = OTP_VARIANTS.DESKTOP,
}) => {
  const completedCodeRef = useRef<string>('');
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleValueChange = (newValue: string) => {
    // Clear error when user starts typing
    if (onErrorClear && error && newValue.length > 0) {
      onErrorClear();
    }

    // If the value is being cleared or changed, reset the completed ref
    if (newValue.length < length) {
      completedCodeRef.current = '';
    }

    // Call onChange first
    onChange(newValue);
    // Check if OTP is complete and hasn't been processed yet
    if (
      newValue.length === length &&
      /^\d+$/.test(newValue) &&
      completedCodeRef.current !== newValue
    ) {
      // Mark as completed immediately (synchronously) to prevent duplicate calls
      completedCodeRef.current = newValue;

      // Blur the input to hide the cursor when all fields are filled
      setTimeout(() => {
        // Try to find the hidden input element used by input-otp
        const hiddenInput = containerRef.current?.querySelector(
          'input[type="text"]'
        ) as HTMLInputElement;
        if (hiddenInput) {
          hiddenInput.blur();
        }
        // Also blur the active element as fallback
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, 0);

      // Call onComplete directly when all digits are entered
      if (onComplete) {
        setTimeout(() => {
          onComplete();
        }, 0);
      }
    }
  };

  // Base classes that should always be applied
  // Note: Focus state is handled by isActive from input-otp context
  const baseSlotClasses = clsx(
    'flex items-center justify-center text-center font-geist font-normal rounded-xl border outline-none transition-colors',
    'focus:ring-0 focus-visible:ring-0 focus:outline-none focus-visible:outline-none',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
    'text-base md:text-2xl',
    'p-2', // Padding on each box, not the group
    'leading-none', // Remove line-height to ensure perfect centering
    'selection:bg-blue-200',
    {
      'border-gray-300': !error,
      'border-red-700': error,
    }
  );

  // Height override - h-14 on desktop, h-12 on mobile
  const heightClass = '!h-14 max-md:!h-12';
  // Width classes - each box should take equal width (flex-1 divides equally)
  const widthClasses = 'flex-1 min-w-0'; // Equal width distribution, prevent overflow

  // Slot classes - merge inputClassName with base classes and height override
  const slotClasses = inputClassName
    ? clsx(
        baseSlotClasses,
        inputClassName,
        // Override height: h-14 on desktop, h-12 on mobile
        heightClass,
        // Override width to be adaptive
        widthClasses
      )
    : clsx(
        baseSlotClasses,
        widthClasses,
        variant === OTP_VARIANTS.DESKTOP
          ? 'h-12 md:h-14 p-2 bg-white text-gray-1000 placeholder:text-gray-800 tracking-[-0.16px] leading-none'
          : 'h-14 max-md:h-12 p-2 bg-white text-gray-800 placeholder:text-gray-800 tracking-[-0.16px] leading-none'
      );

  // Determine gap based on variant or use default from className
  // Group should have no padding, only gap between boxes, and take full width on mobile only
  const groupGapClass = clsx(
    variant === OTP_VARIANTS.DESKTOP ? 'gap-2.5' : 'gap-[10px]',
    'p-0', // Ensure no padding on group
    'max-md:w-full' // Group takes full width only on mobile
  );

  return (
    <div ref={containerRef}>
      <InputOTP
        maxLength={length}
        value={value}
        onChange={handleValueChange}
        disabled={disabled}
        autoFocus={autoFocus}
        pattern={REGEXP_ONLY_DIGITS}
        containerClassName={clsx(className, 'max-md:w-full')}
        className="gap-0 p-0 max-md:w-full"
      >
        <InputOTPGroup className={groupGapClass}>
          {Array.from({ length }).map((_, index) => {
            const dataAttributes = {
              [`data-${dataAttribute}`]: index,
            };

            return (
              <SlotWithFocus
                key={index}
                index={index}
                slotClasses={slotClasses}
                error={error}
                dataAttributes={dataAttributes}
              />
            );
          })}
        </InputOTPGroup>
      </InputOTP>
    </div>
  );
};

export default OtpInput;
